import { Input, Modal, Select, Textarea } from '@/components/ui';
import { useAsesoriaById } from '@/hooks/useAsesorias';
import { useEmpresa } from '@/hooks/useEmpresas';
import { useReuniones } from '@/hooks/useReuniones';
import '@/pages/admin/admin.css';
import { empresasService } from '@/services/empresas.service';
import type { Reunion } from '@/types/types';
import { isAsesoriaEditable, normalizeAsesoriaEstado, ASESORIA_READONLY_MESSAGE } from '@/utils/asesoriaStatus';
import type { AsesoriaEstado } from '@/utils/asesoriaStatus';
import type { CSSProperties, ChangeEvent, FormEvent } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type ReunionTimeKey = 'fechaInicio' | 'fechaFin';

// Las reuniones se manejan siempre en hora de Colombia (GMT-5). America/Bogota es un
// offset fijo -05:00 (sin horario de verano), así que estos helpers leen la hora de
// pared colombiana de un instante sin importar la zona horaria del navegador.
const BOGOTA_TZ = 'America/Bogota';

function bogotaDateInput(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: BOGOTA_TZ }); // YYYY-MM-DD
}

function bogotaTimeInput(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: BOGOTA_TZ }); // HH:mm
}

function sameBogotaDay(a: string | null | undefined, b: string | null | undefined): boolean {
  const da = bogotaDateInput(a);
  return da !== '' && da === bogotaDateInput(b);
}

function formatTime(reunion: Reunion, key: ReunionTimeKey): string {
  const dateStr = reunion[key];
  if (!dateStr) return 'N/A';
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) {
    return dateStr;
  }
  return parsed.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: BOGOTA_TZ,
  });
}

function getDiaMes(dateStr: string | null | undefined): {
  dia: string;
  mes: string;
} {
  if (!dateStr) return { dia: '--', mes: '---' };
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return { dia: '--', mes: '---' };
  return {
    dia: parsed.toLocaleDateString('es-CO', { day: '2-digit', timeZone: BOGOTA_TZ }),
    mes: parsed.toLocaleDateString('es-CO', { month: 'short', timeZone: BOGOTA_TZ }).toUpperCase(),
  };
}

function getChipStyle(estado: string): CSSProperties {
  switch (estado) {
    case 'PROGRAMADA':
      return { color: 'var(--admin-blue)', background: 'var(--admin-blue-bg)' };
    case 'COMPLETADA':
      return { color: 'var(--admin-green)', background: 'var(--admin-green-bg)' };
    case 'CANCELADA':
      return { color: 'var(--admin-red)', background: 'var(--admin-red-bg)' };
    default:
      return { color: 'var(--admin-text2)', background: 'var(--admin-surface2)' };
  }
}

const initialFormState = {
  fecha: '',
  horaInicio: '',
  horaFin: '',
  motivo: '',
};

export function ReunionesPage() {
  const { nit, asesoriaId } = useParams<{ nit: string; asesoriaId: string }>();
  const navigate = useNavigate();
  const { data: asesoria, isLoading: isAsesoriaLoading, error: asesoriaError, refetch } = useAsesoriaById(nit ?? '', asesoriaId);
  // Correo de la empresa actual (GET /empresas/:nit), usado como placeholder al agendar.
  const { empresa, refetch: refetchEmpresa } = useEmpresa(nit ?? '');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReunionId, setEditingReunionId] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [formValues, setFormValues] = useState(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ fecha?: string; horaInicio?: string; horaFin?: string }>({});

  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);

  // Error modal state
  const [errorModal, setErrorModal] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: '' });

  const { isSubmitting, crearReunion, actualizarReunion, completarReunion, cancelarReunion } = useReuniones(nit ?? '');
  // "Hoy" en hora de Colombia, para que el mínimo de fecha y la validación de fecha
  // pasada sean consistentes con el resto (GMT-5).
  const getTodayString = () => new Date().toLocaleDateString('en-CA', { timeZone: BOGOTA_TZ });

  const validateForm = () => {
    const errors: { fecha?: string; horaInicio?: string; horaFin?: string } = {};
    const today = getTodayString();

    if (!formValues.fecha) errors.fecha = 'La fecha es requerida';
    else if (formValues.fecha < today) errors.fecha = 'La fecha no puede ser en el pasado';

    if (!formValues.horaInicio) errors.horaInicio = 'La hora de inicio es requerida';
    if (!formValues.horaFin) errors.horaFin = 'La hora de fin es requerida';

    if (formValues.fecha && !errors.horaInicio && !errors.horaFin) {
      const start = new Date(`${formValues.fecha}T${formValues.horaInicio}`);
      const end = new Date(`${formValues.fecha}T${formValues.horaFin}`);
      if (start >= end) errors.horaFin = 'La hora de fin debe ser posterior a la de inicio';
    }

    return errors;
  };

  if (isAsesoriaLoading) {
    return (
      <div className="min-h-screen bg-[#f8f7f4]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-center py-24">
            <div className="w-5 h-5 border-2 border-[#1e5fa8] border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  const reuniones = asesoria?.reuniones || [];
  const errorMessage = asesoriaError;
  // Normalizar (no usar el crudo): una asesoría EN_REVISION ya preaprobada debe leerse como
  // FINALIZADA, si no el mensaje de solo-lectura dice "está en revisión" cuando en realidad ya
  // está resuelta.
  const estadoAsesoria = normalizeAsesoriaEstado({ estado: asesoria?.estado ?? 'EN_CURSO', autoAprobar: asesoria?.autoAprobar });
  const puedeEditar = isAsesoriaEditable(estadoAsesoria);
  const readonlyMsg = !puedeEditar ? ASESORIA_READONLY_MESSAGE[estadoAsesoria] : '';

  const reunionesFiltradas = filtroEstado ? reuniones.filter((r) => r.estado === filtroEstado) : reuniones;

  const stats = {
    total: reuniones.length,
    programadas: reuniones.filter((r) => r.estado === 'PROGRAMADA').length,
    completadas: reuniones.filter((r) => r.estado === 'COMPLETADA').length,
    canceladas: reuniones.filter((r) => r.estado === 'CANCELADA').length,
  };

  if (errorMessage || !asesoria) {
    return (
      <div className="min-h-screen bg-[#f8f7f4]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="admin-card p-12 text-center">
            <p className="text-[13px] text-[#999]">{errorMessage || 'Asesoría no encontrada'}</p>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Las reuniones se agendan en hora de Colombia (GMT-5). Se envía el offset -05:00
  // explícito para que el backend no interprete la hora naive como UTC.
  const combineDateAndTime = (fecha: string, hora: string): string => {
    if (!fecha || !hora) return '';
    return `${fecha}T${hora}:00-05:00`;
  };

  const handleNuevaReunionClick = () => {
    setIsModalOpen(true);
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailError(null);

    const finalEmail = emailValue.trim() || empresa?.correoEmpresa || '';

    if (!finalEmail) {
      setEmailError('El correo electrónico es requerido');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(finalEmail)) {
      setEmailError('Formato de correo electrónico inválido');
      return;
    }

    if (!nit) {
      setEmailError('No se pudo identificar la empresa');
      return;
    }

    setIsEmailSubmitting(true);
    try {
      await empresasService.actualizarCorreoEmpresa(nit, finalEmail);
      await refetch(); // Refetch asesoria to get updated correoEmpresa
      await refetchEmpresa(); // Refetch empresa to get updated correoEmpresa
      setIsEmailModalOpen(false);
      setEmailValue('');
    } catch (error: any) {
      const errorMessage = error?.message || 'Error al actualizar el correo electrónico';
      setEmailError(errorMessage);
    } finally {
      setIsEmailSubmitting(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!nit) {
      setFormError('No se pudo leer los datos de la ruta');
      return;
    }

    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const inicio = combineDateAndTime(formValues.fecha, formValues.horaInicio);
      const fin = combineDateAndTime(formValues.fecha, formValues.horaFin);

      if (editingReunionId) {
        // Modo edición
        await actualizarReunion(editingReunionId, nit, {
          inicio: inicio || undefined,
          fin: fin || undefined,
          motivo: formValues.motivo.trim() ? formValues.motivo.trim() : undefined,
        });
        await refetch();
      } else {
        // Modo creación
        await crearReunion({
          inicio: inicio,
          fin: fin,
          motivo: formValues.motivo.trim() ? formValues.motivo.trim() : undefined,
          companyEmail: asesoria?.correoEmpresa || undefined,
        });
        await refetch();
      }
      setIsModalOpen(false);
      setEditingReunionId(null);
      setFormValues(initialFormState);
    } catch (error: any) {
      setErrorModal({ isOpen: true, message: error?.message || 'Error al procesar la reunión' });
    }
  };

  const handleMarcarCompletada = async (reunionId: string, nit: string) => {
    if (confirm('¿Marcar esta reunión como completada?')) {
      try {
        await completarReunion(reunionId, nit);
        await refetch();
      } catch (error: any) {
        setErrorModal({ isOpen: true, message: error?.message || 'Error al completar la reunión' });
      }
    }
  };

  const handleCancelar = async (reunionId: string, nit: string) => {
    if (confirm('¿Cancelar esta reunión?')) {
      try {
        await cancelarReunion(reunionId, nit);
        await refetch();
      } catch (error: any) {
        setErrorModal({ isOpen: true, message: error?.message || 'Error al cancelar la reunión' });
      }
    }
  };

  const handleEditar = (reunion: Reunion) => {
    const now = new Date();
    const finDate = reunion.fechaFin ? new Date(reunion.fechaFin) : null;

    if (finDate && finDate <= now) {
      alert('No se puede editar una reunión que ya ha finalizado');
      return;
    }

    setEditingReunionId(reunion.reunionId);
    setFormValues({
      fecha: bogotaDateInput(reunion.fechaInicio),
      horaInicio: bogotaTimeInput(reunion.fechaInicio),
      horaFin: bogotaTimeInput(reunion.fechaFin),
      motivo: reunion.motivo || '',
    });
    setFormError(null);
    setFieldErrors({});
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(`/empresa/${nit}/asesoria/${asesoriaId}`)}
          className="flex items-center gap-1.5 text-[12px] text-[#999] hover:text-[#1a1a1a] mb-4 transition-colors"
        >
          ← Volver a asesoría
        </button>

        {/* Header */}
        <div className="admin-card mb-6">
          <div className="admin-card-header" style={{ justifyContent: 'space-between' }}>
            <div>
              <h1 className="text-[16px] font-semibold text-[#1a1a1a]">Reuniones</h1>
              <p className="text-[13px] text-[#999] mt-0.5">{asesoria.empresa.nombre}</p>
              <div className="flex items-center gap-2 mt-1.5 text-[12px] text-[#666]">
                <span>Correo de la empresa:</span>
                <span className="font-medium text-[#1a1a1a]">{asesoria?.correoEmpresa || empresa?.correoEmpresa || 'No registrado'}</span>
                {puedeEditar && (
                  <button
                    type="button"
                    onClick={() => {
                      setEmailValue(asesoria?.correoEmpresa || empresa?.correoEmpresa || '');
                      setEmailError(null);
                      setIsEmailModalOpen(true);
                    }}
                    className="text-[#1e5fa8] hover:underline font-medium ml-1"
                  >
                    (Editar)
                  </button>
                )}
              </div>
            </div>
            {puedeEditar && (
              <button className="admin-btn primary" onClick={handleNuevaReunionClick}>
                + Nueva Reunión
              </button>
            )}
          </div>
          {!puedeEditar && (
            <div className="px-4 pb-4">
              <div
                className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-[12px] font-medium"
                style={{ background: 'var(--admin-amber-bg)', color: 'var(--admin-amber)', border: '1px solid #fde68a' }}
              >
                {readonlyMsg || 'Esta asesoría no puede modificarse.'}
              </div>
            </div>
          )}
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-4 gap-3 mb-6 max-sm:grid-cols-2">
          <div className="admin-card p-4 text-center">
            <div className="text-[22px] font-bold text-[#1a1a1a]">{stats.total}</div>
            <div className="text-[12px] text-[#999] mt-0.5">Total</div>
          </div>
          <div className="admin-card p-4 text-center">
            <div className="text-[22px] font-bold" style={{ color: 'var(--admin-blue)' }}>
              {stats.programadas}
            </div>
            <div className="text-[12px] text-[#999] mt-0.5">Programadas</div>
          </div>
          <div className="admin-card p-4 text-center">
            <div className="text-[22px] font-bold" style={{ color: 'var(--admin-green)' }}>
              {stats.completadas}
            </div>
            <div className="text-[12px] text-[#999] mt-0.5">Completadas</div>
          </div>
          <div className="admin-card p-4 text-center">
            <div className="text-[22px] font-bold" style={{ color: 'var(--admin-red)' }}>
              {stats.canceladas}
            </div>
            <div className="text-[12px] text-[#999] mt-0.5">Canceladas</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-3 mb-4">
          <Select
            label="Estado"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            options={[
              { value: '', label: 'Todos' },
              { value: 'PROGRAMADA', label: 'Programadas' },
              { value: 'COMPLETADA', label: 'Completadas' },
              { value: 'CANCELADA', label: 'Canceladas' },
            ]}
            className="w-48"
          />
        </div>

        {/* Lista de Reuniones */}
        {reunionesFiltradas.length === 0 ? (
          <div className="admin-card p-12 text-center">
            <p className="text-[13px] text-[#999] mb-4">No hay reuniones para mostrar</p>
            {puedeEditar && (
              <button className="admin-btn primary" onClick={handleNuevaReunionClick}>
                Agendar Reunión
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {reunionesFiltradas.map((reunion) => (
              <div key={reunion.reunionId} className="admin-card">
                <div className="p-4 flex gap-4">
                  {/* Fecha destacada */}
                  <div
                    className="shrink-0 w-20 h-16 rounded-lg flex flex-col items-center justify-center px-2"
                    style={{ background: 'var(--admin-blue-bg)' }}
                  >
                    {reunion.fechaInicio &&
                      reunion.fechaFin &&
                      (() => {
                        const inicio = getDiaMes(reunion.fechaInicio);
                        const fin = getDiaMes(reunion.fechaFin);
                        if (!sameBogotaDay(reunion.fechaInicio, reunion.fechaFin)) {
                          return (
                            <div className="flex flex-row items-end justify-center w-full gap-1">
                              <div className="flex flex-col items-center">
                                <span className="text-[18px] font-bold leading-none" style={{ color: 'var(--admin-blue)' }}>
                                  {inicio.dia}
                                </span>
                                <span className="text-[9px] uppercase leading-none" style={{ color: 'var(--admin-blue)' }}>
                                  {inicio.mes}
                                </span>
                              </div>
                              <span className="text-[14px] font-bold" style={{ color: 'var(--admin-blue)' }}>
                                -
                              </span>
                              <div className="flex flex-col items-center">
                                <span className="text-[18px] font-bold leading-none" style={{ color: 'var(--admin-blue)' }}>
                                  {fin.dia}
                                </span>
                                <span className="text-[9px] uppercase leading-none" style={{ color: 'var(--admin-blue)' }}>
                                  {fin.mes}
                                </span>
                              </div>
                            </div>
                          );
                        }
                      })()}
                    {(!reunion.fechaInicio || !reunion.fechaFin || sameBogotaDay(reunion.fechaInicio, reunion.fechaFin)) &&
                      (() => {
                        const inicio = getDiaMes(reunion.fechaInicio);
                        return (
                          <div className="flex flex-col items-center justify-center h-full">
                            <span className="text-[22px] font-bold leading-none" style={{ color: 'var(--admin-blue)' }}>
                              {inicio.dia}
                            </span>
                            <span className="text-[10px] uppercase leading-none mt-0.5" style={{ color: 'var(--admin-blue)' }}>
                              {inicio.mes}
                            </span>
                          </div>
                        );
                      })()}
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[13px] font-medium text-[#1a1a1a]">
                        {formatTime(reunion, 'fechaInicio')} — {formatTime(reunion, 'fechaFin')}
                      </span>
                      <span className="admin-chip" style={getChipStyle(reunion.estado)}>
                        {reunion.estado}
                      </span>
                    </div>

                    <p className="text-[13px] text-[#666] mb-3 truncate">{reunion.motivo || 'Sin motivo especificado'}</p>

                    {reunion.estado === 'PROGRAMADA' && puedeEditar && (
                      <div className="flex gap-2 flex-wrap">
                        <button
                          className="admin-btn"
                          style={{ color: 'var(--admin-green)', borderColor: 'var(--admin-green-bg)', background: 'var(--admin-green-bg)' }}
                          onClick={() => handleMarcarCompletada(reunion.reunionId, asesoria.empresa.nit)}
                        >
                          Completar
                        </button>
                        <button
                          className="admin-btn"
                          style={{ color: 'var(--admin-red)', borderColor: 'var(--admin-red-bg)', background: 'var(--admin-red-bg)' }}
                          onClick={() => handleCancelar(reunion.reunionId, asesoria.empresa.nit)}
                        >
                          Cancelar
                        </button>
                        {reunion.fechaFin && new Date(reunion.fechaFin) > new Date() && (
                          <button className="admin-btn" onClick={() => handleEditar(reunion)}>
                            Editar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Correo Empresa */}
        <Modal
          isOpen={isEmailModalOpen}
          onClose={() => {
            if (asesoria?.correoEmpresa || empresa?.correoEmpresa) {
              setIsEmailModalOpen(false);
            }
          }}
          title={asesoria?.correoEmpresa || empresa?.correoEmpresa ? 'Editar Correo de la Empresa' : 'Correo de la Empresa Requerido'}
          size="md"
        >
          <form className="space-y-4" onSubmit={handleEmailSubmit}>
            <p className="text-[13px] text-[#666] mb-4">
              Para agendar reuniones, es necesario registrar el correo electrónico de la empresa. Este correo se utilizará para enviar las
              invitaciones y recordatorios de las reuniones.
            </p>
            <Input
              name="email"
              type="email"
              label={
                <>
                  Correo Electrónico de la Empresa <span className="text-red-500">*</span>
                </>
              }
              placeholder={empresa?.correoEmpresa || 'ejemplo@empresa.com'}
              required={!empresa?.correoEmpresa}
              value={emailValue}
              onChange={(e) => {
                setEmailValue(e.target.value);
                setEmailError(null);
              }}
            />

            {emailError && <p className="text-[12px] text-[#c0392b] mt-1">{emailError}</p>}

            <div className="flex justify-end gap-3 pt-4">
              {(asesoria?.correoEmpresa || empresa?.correoEmpresa) && (
                <button type="button" onClick={() => setIsEmailModalOpen(false)} className="admin-btn" disabled={isEmailSubmitting}>
                  Cancelar
                </button>
              )}
              <button
                type="submit"
                className="admin-btn primary"
                disabled={isEmailSubmitting || (!emailValue.trim() && !empresa?.correoEmpresa)}
              >
                {isEmailSubmitting ? 'Guardando...' : 'Guardar Correo'}
              </button>
            </div>
          </form>
        </Modal>

        {/* Modal Nueva / Editar Reunión */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingReunionId(null);
            setFormValues(initialFormState);
            setFormError(null);
            setFieldErrors({});
          }}
          title={editingReunionId ? 'Editar Reunión' : 'Agendar Nueva Reunión'}
          size="md"
        >
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Input
              name="fecha"
              type="date"
              label={
                <>
                  Fecha <span className="text-red-500">*</span>
                </>
              }
              required
              min={getTodayString()}
              value={formValues.fecha}
              onChange={handleInputChange}
              error={fieldErrors.fecha}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                name="horaInicio"
                type="time"
                label={
                  <>
                    Hora Inicio <span className="text-red-500">*</span>
                  </>
                }
                required
                value={formValues.horaInicio}
                onChange={handleInputChange}
                error={fieldErrors.horaInicio}
              />
              <Input
                name="horaFin"
                type="time"
                label={
                  <>
                    Hora Fin <span className="text-red-500">*</span>
                  </>
                }
                required
                value={formValues.horaFin}
                onChange={handleInputChange}
                error={fieldErrors.horaFin}
              />
            </div>
            <Textarea
              name="motivo"
              label="Motivo"
              placeholder="Describe el motivo de la reunión..."
              rows={3}
              value={formValues.motivo}
              onChange={handleInputChange}
            />

            {formError && <p className="text-[12px] text-[#c0392b] mt-1">{formError}</p>}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                className="admin-btn"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingReunionId(null);
                  setFormValues(initialFormState);
                  setFormError(null);
                  setFieldErrors({});
                }}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button type="submit" className="admin-btn primary" disabled={isSubmitting}>
                {isSubmitting
                  ? editingReunionId
                    ? 'Actualizando...'
                    : 'Agendando...'
                  : editingReunionId
                    ? 'Actualizar Reunión'
                    : 'Agendar Reunión'}
              </button>
            </div>
          </form>
        </Modal>

        {/* Modal de Error */}
        <Modal isOpen={errorModal.isOpen} onClose={() => setErrorModal({ isOpen: false, message: '' })} title="Error" size="md">
          <div className="space-y-4">
            <div
              className="flex items-start gap-3 rounded-lg p-4"
              style={{ background: 'var(--admin-red-bg)', border: '1px solid #f5c6c2' }}
            >
              <p className="text-[13px]" style={{ color: 'var(--admin-red)' }}>
                {errorModal.message}
              </p>
            </div>
            <div className="flex justify-end">
              <button className="admin-btn primary" onClick={() => setErrorModal({ isOpen: false, message: '' })}>
                Cerrar
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
